import {Point} from "../entities/Point";
import {Vector} from "../entities/Vector";

import {IPoint} from "../interfaces/IPoint";
import {IVector} from "../interfaces/IVector";
import {PointUtils} from "./PointUtils";

export class Geodesic {

    public static projectOnVector(duet: IPoint, duetVector: IVector) {
        let result = null;
        if (duetVector.start.lat === duetVector.end.lat) {
            result = [duetVector.start.lat, duet.lon];
        } else if (duetVector.start.lon === duetVector.end.lon) {
            result = [duet.lat, duetVector.start.lon];
        } else {
            const acroissement = (duetVector.end.lon - duetVector.start.lon) /
                (duetVector.end.lat - duetVector.start.lat);
            const origine = duetVector.start.lon - duetVector.start.lat * acroissement;
            const perpAcroissement = -1 / acroissement;
            const perpOrigine = duet.lon - duet.lat * perpAcroissement;
            const xProj = (perpOrigine - origine) / (acroissement - perpAcroissement);
            const yProj = perpAcroissement * xProj + perpOrigine;
            result = [xProj, yProj];
        }
        return new Point(result[0], result[1]);
    }

    public static pointListMetersLen(points: IPoint[]) : number {
        let totalLen:number = 0;
        for(let i = 0; i < points.length - 1; i++) {
            totalLen = totalLen + Geodesic.metersBetween(points[i], points[i+1]);
        }
        return totalLen;
    }

    public static geoBounds(pointList: IPoint[]) {
        let minLat = Math.min.apply(null, pointList.map(e => e.lat));
        let maxLat = Math.max.apply(null, pointList.map(e => e.lat));
        let minLon = Math.min.apply(null,pointList.map(e => e.lon));
        let maxLon = Math.max.apply(null, pointList.map(e => e.lon));

        return {
            south: minLat,
            north: maxLat,
            west: minLon,
            east: maxLon,
            southWest: [minLat, minLon],
            northEst: [maxLat, maxLon]
        }
    }

    public static distanceArray(pointList: IPoint[]) {
        let result: number[] = [0];
        for(let i = 1; i < pointList.length; i++) {
            result.push(Geodesic.metersBetween(pointList[i-1], pointList[i]) + result[i-1]);
        }
        return result;
    }

    /**
     * Compute the relative distance matrix of each point
     * @param pointList
     */
    public static relativeDistanceMatrix(pointList: IPoint[]) {
        let result: number[][] = Array(pointList.length).fill(null).map(_ => Array(pointList.length).fill(0)); //Creates the matrix
        for(let i = 0; i < pointList.length; i++) {
            for(let j = i + 1; j < pointList.length; j++) {
                result[i][j] = Geodesic.metersBetween(pointList[i], pointList[j]);
                // result[j][i] = result[i][j]; //Since the matrix is diagonally symmetric, we COULD fill it this way
            }

        }
        return result;
    }

    public static metersBetween(duet1: IPoint, duet2: IPoint) : number {
        let lat1 = duet1.lat;
        let lng1 = duet1.lon;
        let lat2 = duet2.lat;
        let lng2 = duet2.lon;

        const pi80 = Math.PI / 180;
        lat1 *= pi80;
        lng1 *= pi80;
        lat2 *= pi80;
        lng2 *= pi80;

        /**
         * originally used value : 6372.797
         * Since we're operating around the 48" lat value, I selected a more accurate value here :
         * https://rechneronline.de/earth-radius/
         *
         * @type {number}
         */
        const r = 6366.371;
        const dlat = lat2 - lat1;
        const dlng = lng2 - lng1;
        const a = Math.sin(dlat / 2) * Math.sin(dlat / 2) +
            Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlng / 2) * Math.sin(dlng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const km = r * c;

        return (km * 1000);
    }

    public static isOnVector(point: IPoint, vector: IVector) {
        if(PointUtils.equals(point,vector.start) || PointUtils.equals(point, vector.end)) {
            return true // If the point is one or another end of the vector, it's ok.
        }
        const startToPointVector = new Vector(vector.start, point); //From vector start up to point.
        if(Geodesic.areAlmostCollinear(startToPointVector, vector)) {
            const sp = Geodesic.normalizedScalarProduct(startToPointVector, vector);
            return (sp <= 1 && sp >= 0);
        }
        return false;
    }

    /**
     * Returns true only if the point is  before the vector
     * @param duet
     * @param duetVector
     * @returns {boolean}
     */
    public static isBeforeVector(duet: IPoint, duetVector: IVector) {
        const divergentVector = new Vector(duetVector.start, duet);
        if (Geodesic.areAlmostCollinear(divergentVector, duetVector)) {
            const sp = Geodesic.normalizedScalarProduct(divergentVector, duetVector);
            return sp < 0;
        }
        return false;
    }

    /**
     * Returns true only if the point is after the vector
     * @param duet
     * @param duetVector
     * @returns {boolean}
     */
    public static isAfterVector(duet: IPoint, duetVector: IVector) {
        const divergentVector = new Vector(duetVector.start, duet);
        if(Geodesic.areAlmostCollinear(divergentVector, duetVector)) {
            const sp = Geodesic.normalizedScalarProduct(divergentVector, duetVector);
            return sp > 1;
        }
        return false;
    }

    public static computeHeading(duetVector:IVector) {
        const fromLat = Geodesic.toRadians(duetVector.start.lat);
        const fromLng = Geodesic.toRadians(duetVector.start.lon);
        const toLat = Geodesic.toRadians(duetVector.end.lat);
        const toLng = Geodesic.toRadians((duetVector.end.lon));
        const dLng = toLng - fromLng;
        const heading = Math.atan2(
            Math.sin(dLng) * Math.cos(toLat),
            Math.cos(fromLat) * Math.sin(toLat) - Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLng),
        );
        return Geodesic.wrap(Geodesic.toDegrees(heading), -180, 180);
    }

    public static toLinearlyNoised(pointList: IPoint[]) {
        //First we densify the shape to have 1 point every 2 meters at least.
        let densified = Geodesic.increaseDuetListDensity(pointList, 2);

        for(let i = 0; i < densified.length; i++) {
            densified[i].lat = densified[i].lat + (Math.random() - 0.5) * 1E-4;
            densified[i].lon = densified[i].lon + (Math.random() - 0.5) * 1E-4;
        }
        return densified;
        //We will add a noise between -10 and 10 meters approximatly

    }


    /**
     *
     * @param referenceDuetVector the reference vector to project the target on
     * @param targetDuetVector the targetted vector to project on the reference
     * @returns {number}
     */
    public static normalizedScalarProduct(targetDuetVector: IVector, referenceDuetVector: IVector) {
        const a = {
            x: referenceDuetVector.end.lat - referenceDuetVector.start.lat,
            y: referenceDuetVector.end.lon - referenceDuetVector.start.lon,
        };
        const b = {
            x: targetDuetVector.end.lat - targetDuetVector.start.lat,
            y: targetDuetVector.end.lon - targetDuetVector.start.lon,
        };
        const scalarProduct = a.x * b.x + a.y * b.y;
        const aNorm = Math.sqrt(a.x * a.x + a.y * a.y);
        return scalarProduct / (aNorm * aNorm);
    }

    /**
     * returns wether the vectors are co linear (tolerance 10^-2)
     *
     * @param duetVector1
     * @param duetVector2
     * @returns {boolean}
     */
    public static areAlmostCollinear(duetVector1: IVector, duetVector2: IVector): boolean {
        // const h1 = Geodesic.computeHeading(duetVector1);
        // const h2 = Geodesic.computeHeading(duetVector2);

        const a = {
            x: duetVector1.end.lat - duetVector1.start.lat,
            y: duetVector1.end.lon - duetVector1.start.lon,
        };
        const b = {
            x: duetVector2.end.lat - duetVector2.start.lat,
            y: duetVector2.end.lon - duetVector2.start.lon,
        };

        if((a.x === 0 && b.x === 0) || (a.y === 0 && b.y === 0)) {
            return true; // a & b are vertical or horizontal
        } else if(b.x === 0 || b.y === 0) {
            return false; // b is vertical or horizontal, but as is from another kind.
        }
        const k1 = a.x/b.x;
        const k2 = a.y/b.y;
        return (Math.round(k2 * 1000000) === Math.round(k1 * 1000000));
    }

    /**
     * Densifies a list of duets with a minimum of 1 meter density between each duet.
     * @param initialDuetList
     * @param minimumMetersDensity
     * @returns {[]}
     */
    public static increaseDuetListDensity(initialDuetList: IPoint[], minimumMetersDensity: number): IPoint[] {
        if(initialDuetList.length === undefined || initialDuetList.length < 2) {
            console.warn("Caution : increaseDuetListDensity was invoked with a wrong initial duet list", initialDuetList);
            console.trace();
        }
        const denseDuetList = [];
        const includeAllInitialCPS = true;  // This function must include initial CPS.
        for(let j = 0; j < initialDuetList.length - 1; j++) {
            const a  = initialDuetList[j];
            const b = initialDuetList[j+1];
            const delta = Geodesic.metersBetween(a, b);
            const xA = a.lat;
            const yA = a.lon;
            const xAB = b.lat - a.lat;
            const yAB = b.lon - a.lon;
            const k =  Math.floor(delta/minimumMetersDensity);
            if(k === 0) {
                if(delta > minimumMetersDensity / 2) {
                    denseDuetList.push(a);
                } else if(delta > 1E-4 && includeAllInitialCPS) {
                    denseDuetList.push(a);
                    // If includeAllInitialCPS is false, we will have a polyline that is matching the desired density,
                    // i.e. we'll remove points form the cps set that are too close regarding to the desired density. This behavior is efficient to define sub engines.
                } else {
                    // (Almost?) collapsed points detected, because they are closer than 1E-4.
                }
            } else {
                denseDuetList.push(a);
                for(let l = 1; l < k; l++) {
                    const xDens = xA + ((l/k)* xAB);
                    const yDens = yA + ((l/k)* yAB);
                    denseDuetList.push(new Point(xDens, yDens));
                }
            }
            if(j === initialDuetList.length -2) {
                // Tail of computation, adding last point
                denseDuetList.push(b);
            }
        }

        return denseDuetList;

    }

    public static  toRadians(degrees : number) {
        return degrees * Math.PI / 180;
    }

    public static  toDegrees(radians: number) {
        return radians * 180 / Math.PI;
    }

    public static  mod(x: number, m: number) {
        return ((x % m) + m) % m;
    }

    public static  wrap(n: number, min: number, max: number) {
        return (n >= min && n < max) ? n : (Geodesic.mod(n - min, max - min) + min);
    }

    public static getStandardDeviation(array:number[]) {
        const n = array.length
        const mean = array.reduce((a, b) => a + b) / n
        return Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n)
    }

}
